import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { GripVertical, Trash2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, photoUrl } from "@/lib/api";
import { Card, btn, inputCls } from "@/components/common/Ornament";

function PhotoRow({ p, i, onDrag, onDrop, onSave, onDelete }) {
  const [cap, setCap] = useState(p.caption);
  return (
    <div draggable onDragStart={() => onDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
      className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-md border border-[#E2DCD2] bg-white p-2 hover:border-[#C59B27] transition-colors" data-testid={`admin-photo-row-${i}`}>
      <div className="flex items-center gap-3">
        <GripVertical className="h-5 w-5 text-slate-400 cursor-grab shrink-0" data-testid={`admin-photo-drag-${i}`} />
        <img src={photoUrl(p.id)} alt="" className="h-14 w-20 object-cover rounded" />
      </div>
      <input value={cap} onChange={(e) => setCap(e.target.value)} onBlur={() => cap !== p.caption && onSave(p, { caption: cap })} className={inputCls + " flex-1"} data-testid={`admin-photo-caption-${i}`} />
      <select value={p.category} onChange={(e) => onSave(p, { category: e.target.value })} className={inputCls + " sm:w-40"} data-testid={`admin-photo-category-${i}`}>
        <option value="gallery">Gallery photo</option>
        <option value="plan">Plan / rendering</option>
      </select>
      <button onClick={() => onDelete(p)} className={btn.danger} data-testid={`admin-photo-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export default function PhotosTab() {
  const { data, mutate } = useSWR("/photos");
  const [list, setList] = useState([]);
  const [cat, setCat] = useState("gallery");
  const [busy, setBusy] = useState(false);
  const drag = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (data) setList(data);
  }, [data]);

  const upload = async (files) => {
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", cat);
        await api.post("/admin/photos", fd);
      }
      toast.success(`${files.length} photo(s) uploaded`);
      mutate();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const onDrop = async (to) => {
    const from = drag.current;
    if (from === null || from === to) return;
    const next = [...list];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setList(next);
    drag.current = null;
    try { await api.put("/admin/photos-order", { ids: next.map((p) => p.id) }); mutate(); toast.success("Order saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const onSave = async (p, patch) => {
    try { await api.put(`/admin/photos/${p.id}`, { caption: p.caption, category: p.category, ...patch }); mutate(); toast.success("Photo updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async (p) => {
    if (!window.confirm(`Delete “${p.caption}”?`)) return;
    try { await api.delete(`/admin/photos/${p.id}`); mutate(); toast.success("Photo deleted"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Card eyebrow="Property Page" title="Photos, Renderings & Plans" testId="admin-photos">
      <div className="flex flex-col sm:flex-row gap-2 mb-6">
        <select value={cat} onChange={(e) => setCat(e.target.value)} className={inputCls + " sm:w-52"} data-testid="admin-photo-upload-category">
          <option value="gallery">Upload as gallery photo</option>
          <option value="plan">Upload as plan / rendering</option>
        </select>
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" multiple hidden onChange={(e) => e.target.files.length && upload([...e.target.files])} data-testid="admin-photo-file-input" />
        <button onClick={() => fileRef.current.click()} disabled={busy} className={btn.primary} data-testid="admin-photo-upload-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload images</button>
      </div>
      <p className="text-xs text-slate-500 mb-3">Drag rows to reorder. Captions save when you click away.</p>
      <div className="space-y-2">
        {list.map((p, i) => <PhotoRow key={p.id} p={p} i={i} onDrag={(x) => { drag.current = x; }} onDrop={onDrop} onSave={onSave} onDelete={onDelete} />)}
      </div>
    </Card>
  );
}
